// @ts-nocheck
/**
 * Shared product-detail popup for browse surfaces that don't load
 * registry-unified.js (the registry-browse "See all" page, the registry-start
 * intake walkthrough).
 *
 * Mirrors the popup in registry-unified.js: a two-column card on desktop /
 * full-height bottom sheet on mobile, reusing the same `re-detail` styles from
 * registry.css and the same /apps/registry/api/product-detail descriptor
 * endpoint. Cards opt in by carrying a `data-detail-*` descriptor on their
 * card root; call BirchDetailPopup.openFromCard(cardEl, hooks).
 *
 * VARIANT PICKER (parity with registry-edit). When the host passes `hooks`
 * (see below) the popup also renders the option grid — one swatch row per
 * option (Color, Size, …) plus a single Add/Remove/Confirm CTA for the selected
 * combination — element for element the same as registry-unified.js's. Variants
 * come from:
 *   • /registries/:id/items/:itemId/variants  when the card is already on the
 *     registry (hooks.anchorItemId) — that response carries selectedVariantId,
 *     which is what tells Remove from Confirm.
 *   • /product-variants?source=…              otherwise (descriptor-keyed; the
 *     card isn't a registry item yet, so nothing is bound).
 * Without hooks the popup behaves exactly as before: detail only, no picker.
 *
 * DESCRIPTOR (what open() takes; descriptorFromCard builds one from a card's
 * data-detail-* attributes, and any caller may build one by hand):
 *   {
 *     source: 'shopify' | 'external',   // REQUIRED — anything else is a no-op
 *     title, imageUrl, brand,           // strings
 *     priceCents,                       // integer cents, or null
 *     currency,                         // default 'USD'
 *     shopifyHandle, shopifyProductId,  // shopify keying (PDP link + variants)
 *     externalUrl, lushlifeProductId,   // external keying (D1 enrichment)
 *     externalRetailer, productType,    // used by the picker / add payload
 *     trustScore,                       // number 0-100, or null
 *     detail,                           // OPTIONAL pre-known detail — see below
 *   }
 *
 * SEEDED DETAIL (`descriptor.detail`, CUR-80 Task 15). The popup normally knows
 * only what a card carries and fetches the rest. A caller that ALREADY holds the
 * catalog record (the guided flow holds the whole ProductCacheEntry for a Birch
 * pick) can hand it over in the same normalized shape the detail endpoint
 * returns — { brand, image, price, currency, trustScore, trustBreakdown,
 * keyFeatures[], pros[], cons[], specifications[[label,value]],
 * certifications[], materials[], description, externalUrl } — and the popup
 * paints it IMMEDIATELY instead of showing a skeleton. Then:
 *   • a COMPLETE seed (a description plus at least one of features/pros/cons/
 *     specs) skips the network entirely — zero requests;
 *   • a PARTIAL seed is still painted first and the fetch MERGES over it, so a
 *     field the caller didn't have is filled in and a field it did have is never
 *     blanked by a thinner response;
 *   • a failed fetch leaves a real seed on screen rather than replacing it with
 *     "we couldn't load extra details".
 * Omit it and the popup behaves exactly as it always has.
 *
 * hooks = {
 *   registryId,                  // required for the picker to render
 *   anchorItemId,                // registry item this card maps to, or null
 *   getItems: () => items[],     // live registry items (for Add⇄Remove state)
 *   onAdded:   (item, payload) => {},
 *   onRemoved: (itemId) => {},
 *   onConfirmed: (item) => {},   // variant resolved in place on an existing item
 *   hideExternalLink,            // true: suppress the "View at {retailer}" buy
 *                                 // link for an external-sourced item (gift-
 *                                 // giver view — CUR-39: description/details
 *                                 // only, no route out to the retailer). A
 *                                 // Shopify item's own "View product" link is
 *                                 // unaffected — that's Birch's own PDP, not
 *                                 // a retailer URL.
 * }
 *
 * Exposes window.BirchDetailPopup = { open, openFromCard, descriptorFromCard }.
 */
(function () {
  "use strict";

  const escFn = () =>
    (window.BirchRegistry && window.BirchRegistry.escapeHtml) ||
    ((s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])));

  // Read the normalized data-detail-* descriptor a browse card carries on its
  // card root. Returns null when the card has no usable source.
  //
  // The trailing fields (retailer / productType / product + variant ids) are not
  // rendered by the popup body — they're what the variant picker needs to look
  // options up and to build a faithful add payload. Cards that don't carry them
  // still open; they just get no picker.
  function descriptorFromCard(card) {
    if (!card) return null;
    const d = card.dataset;
    const source = d.detailSource;
    if (source !== "shopify" && source !== "external") return null;
    const cents = d.detailPriceCents ? parseInt(d.detailPriceCents, 10) : null;
    return {
      source,
      title: d.detailTitle || "",
      imageUrl: d.detailImage || "",
      brand: d.detailBrand || "",
      priceCents: Number.isFinite(cents) ? cents : null,
      currency: d.detailCurrency || "USD",
      shopifyHandle: d.detailHandle || "",
      externalUrl: d.detailUrl || "",
      lushlifeProductId: d.detailLlid || "",
      trustScore: d.detailTrust ? Number(d.detailTrust) : null,
      shopifyProductId: d.productId || "",
      externalRetailer: d.externalRetailer || d.detailRetailer || "",
      productType: d.productType || "",
    };
  }

  // Build the descriptor query string for a browse card (a product that isn't a
  // registry item). Mirrors proxy.api.product-detail.jsx's params.
  function descriptorDetailUrl(item) {
    const params = new URLSearchParams();
    params.set("source", item.source);
    if (item.shopifyHandle) params.set("handle", item.shopifyHandle);
    if (item.lushlifeProductId) params.set("lushlifeId", item.lushlifeProductId);
    if (item.externalUrl) params.set("externalUrl", item.externalUrl);
    if (item.title) params.set("title", item.title);
    if (item.imageUrl) params.set("image", item.imageUrl);
    if (item.brand) params.set("brand", item.brand);
    const cents = typeof item.priceCents === "number"
      ? item.priceCents
      : (item.price != null ? Math.round(Number(item.price) * 100) : null);
    if (cents != null && Number.isFinite(cents)) params.set("price", String(cents / 100));
    if (item.currency) params.set("currency", item.currency);
    if (item.trustScore != null) params.set("trustScore", String(item.trustScore));
    return `/apps/registry/api/product-detail?${params.toString()}`;
  }

  // The /product-variants descriptor for a card that isn't a registry item.
  // Returns null when the card carries nothing the catalog can be keyed on.
  function variantDescriptor(item) {
    if (item.source === "shopify") {
      if (!item.shopifyHandle && !item.shopifyProductId) return null;
      return { source: "shopify", handle: item.shopifyHandle, productId: item.shopifyProductId };
    }
    if (!item.lushlifeProductId && !item.externalUrl) return null;
    return {
      source: "external",
      lushlifeId: item.lushlifeProductId,
      externalUrl: item.externalUrl,
      retailer: item.externalRetailer,
    };
  }

  // ── Seeded detail (descriptor.detail) ──────────────────────────────────────

  /** Layer `next` over `base`, field by field, keeping whatever `base` already
   *  had wherever `next` says nothing. An EMPTY array or a blank string is
   *  "nothing" here — that is the whole point: the detail endpoint answers with
   *  `keyFeatures: []` for a product it couldn't enrich, and letting that
   *  overwrite a real list the caller already held would delete true content. */
  function mergeDetail(base, next) {
    if (!next || typeof next !== "object") return base;
    const out = Object.assign({}, base);
    Object.keys(next).forEach((k) => {
      const v = next[k];
      if (v == null) return;
      if (Array.isArray(v)) { if (v.length) out[k] = v; return; }
      if (typeof v === "string") { if (v.trim()) out[k] = v; return; }
      out[k] = v;
    });
    return out;
  }

  /** Is this "description" nothing but a label?
   *
   *  Some catalogue records carry a synthesised text-match string in the
   *  description field rather than copy — a brand, a full stop and a category
   *  slug: "HARPPA. convertible-bassinets". Under the heading "Description"
   *  that reads as broken data, which is exactly what the CUR-80 UI audit
   *  caught (JUDGE-FINAL.md, HIGH). The guided flow now filters its own picks
   *  at source (guided-registry-recs.js's partnerDescription, which can compare
   *  against the record's separate brand/productType fields); this is the net
   *  under every OTHER caller and under the detail fetch, neither of which
   *  carries a productType to compare with.
   *
   *  The test is what survives taking the record's OWN brand off the front:
   *  real copy is a sentence and has a space in it; a slug is a single
   *  hyphenated token, and a bare brand leaves nothing at all.
   *
   *  Whole-string only. Nothing is ever rewritten or trimmed for display — the
   *  section is the record's own words, or it is absent. */
  function labelNotDescription(text, brand) {
    const raw = String(text == null ? "" : text).trim();
    if (!raw) return true;
    let rest = raw;
    const b = String(brand == null ? "" : brand).trim();
    if (b && rest.slice(0, b.length).toLowerCase() === b.toLowerCase()) {
      rest = rest.slice(b.length).replace(/^[\s.,:;–—-]+/, "");
    }
    return !/\s/.test(rest);
  }

  /** A detail record as the sheet is willing to show it: same object, minus a
   *  description that isn't one. Applied to the seed and to the fetch response
   *  BEFORE either is merged, so the dropped string can't make a thin record
   *  look complete enough to skip the fetch (detailIsComplete, below). */
  function cleanDetail(d) {
    if (!d || typeof d !== "object") return d;
    if (d.description == null || d.description === "") return d;
    if (!labelNotDescription(d.description, d.brand)) return d;
    const out = Object.assign({}, d);
    out.description = null;
    return out;
  }

  const listLen = (v) => (Array.isArray(v) ? v.length : 0);

  /** Is there anything here a reader would call a detail? Decides whether the
   *  body paints content or the loading skeleton. */
  function detailHasContent(d) {
    if (!d) return false;
    return !!(String(d.description || "").trim()
      || listLen(d.keyFeatures) || listLen(d.pros) || listLen(d.cons)
      || listLen(d.specifications) || listLen(d.certifications) || listLen(d.materials));
  }

  /** Complete ENOUGH to skip the fetch: a description AND at least one
   *  structured block. Certifications/materials alone are not enough — those
   *  are the two fields a thin (search-metadata) record carries, and a sheet
   *  with chips and no description is exactly the half-blind view this exists
   *  to fix. */
  function detailIsComplete(d) {
    if (!d) return false;
    if (!String(d.description || "").trim()) return false;
    return !!(listLen(d.keyFeatures) || listLen(d.pros) || listLen(d.cons) || listLen(d.specifications));
  }

  // Everything inside the sheet a Tab can land on. `summary` is in the list
  // because the TrustScore breakdown's disclosure is one.
  const FOCUSABLE_SEL = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function open(descriptor, hooks) {
    if (!descriptor || (descriptor.source !== "shopify" && descriptor.source !== "external")) return;
    const esc = escFn();
    const item = descriptor;
    const detailUrl = descriptorDetailUrl(item);
    const BR = window.BirchRegistry;
    // The picker needs a registry to add to and an API to reach; without either
    // the popup stays detail-only (unchanged behaviour for hook-less callers).
    const wantVariants = !!(hooks && hooks.registryId && BR && BR.API);

    const backdrop = document.createElement("div");
    backdrop.className = "registry-modal-backdrop re-detail-backdrop";
    backdrop.innerHTML = `
      <div class="re-detail" role="dialog" aria-modal="true" aria-label="${esc(item.title || "Product")} details">
        <button type="button" class="re-detail__close" data-detail-close aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="re-detail__grip" aria-hidden="true"></div>
        <div class="re-detail__scroll" data-detail-scroll></div>
      </div>`;
    document.body.appendChild(backdrop);
    document.body.style.overflow = "hidden";

    const scrollHost = backdrop.querySelector("[data-detail-scroll]");
    const dialog = backdrop.querySelector(".re-detail");
    const closeBtn = backdrop.querySelector("[data-detail-close]");
    // Whatever the opener was focused on (the card's image or title button, a
    // bundle row) — focus goes back there on close, so a keyboard shopper lands
    // where they left off instead of at the top of the document.
    const opener = document.activeElement;

    const focusable = () => Array.prototype.filter.call(
      backdrop.querySelectorAll(FOCUSABLE_SEL),
      (el) => el.getClientRects().length > 0,
    );
    const focusIt = (el) => { if (el && el.focus) { try { el.focus({ preventScroll: true }); } catch (_e) { el.focus(); } } };

    const close = () => {
      backdrop.remove();
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
      // `document.body` means the sheet was opened with nothing focused (a
      // programmatic open); focusing it back would just be a blur.
      if (opener && opener !== document.body && opener.isConnected) focusIt(opener);
    };
    // Escape closes; Tab is trapped inside the sheet. A modal that leaks focus
    // to the page behind it is a modal only for people using a mouse.
    const onKey = (e) => {
      if (e.key === "Escape") { close(); return; }
      if (e.key !== "Tab") return;
      const list = focusable();
      if (!list.length) { e.preventDefault(); focusIt(dialog); return; }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (!backdrop.contains(active)) { e.preventDefault(); focusIt(e.shiftKey ? last : first); return; }
      if (e.shiftKey && active === first) { e.preventDefault(); focusIt(last); }
      else if (!e.shiftKey && active === last) { e.preventDefault(); focusIt(first); }
    };
    document.addEventListener("keydown", onKey);
    backdrop.addEventListener("click", (e) => { if (e.target === backdrop) close(); });
    closeBtn.addEventListener("click", close);
    dialog.setAttribute("tabindex", "-1");
    focusIt(closeBtn);

    const cardFallback = {
      source: item.source,
      title: item.title,
      brand: item.brand || null,
      image: item.imageUrl || null,
      price: item.price != null ? Number(item.price) : (typeof item.priceCents === "number" ? item.priceCents / 100 : null),
      currency: item.currency || "USD",
      trustScore: item.trustScore != null ? Number(item.trustScore) : null,
      // The card only carries the overall number — the per-criterion breakdown
      // comes from the detail fetch. Null here keeps renderTrustBlock honest
      // while it's loading rather than drawing empty bars.
      trustBreakdown: null,
      keyFeatures: [], pros: [], cons: [], specifications: [],
      certifications: [], materials: [], description: null,
      externalUrl: item.externalUrl || null,
    };

    // Re-paintable state: the detail fetch and the variant fetch resolve
    // independently and each triggers a repaint from the latest of both. The
    // popup is never empty — it starts from what the card already knows, plus
    // whatever pre-known detail the caller handed over (see the seed note in
    // the header comment).
    const seed = (descriptor.detail && typeof descriptor.detail === "object") ? cleanDetail(descriptor.detail) : null;
    let lastDetail = seed ? mergeDetail(cardFallback, seed) : cardFallback;
    let lastState = detailHasContent(lastDetail) ? "ready" : "loading";
    // A caller that already holds the whole record costs the shopper nothing to
    // read it. A partial seed still fetches — it is the fetch that carries the
    // description and specs a search-metadata projection never had.
    const seedComplete = detailIsComplete(lastDetail);
    let variantData = null;
    // Selected option combo for the variant grid (option name → value). Seeded
    // once variantData resolves; updated in place by option-pill clicks so the
    // choice survives repaints.
    let variantSelection = null;
    // The registry item this popup is anchored to, if any. Set from the host and
    // updated as the registrant adds/removes from inside the popup.
    let anchorItemId = (hooks && hooks.anchorItemId) || null;
    // CUR-213 — whether the option lookup has answered (or will never run): a
    // Birch product's plain Add waits for it, since it may turn out to have options.
    let variantsSettled = !wantVariants;
    // Review I3 — set while the sheet's own Add is in flight.
    let adding = false;

    const repaint = () => {
      if (!backdrop.isConnected) return;
      // A repaint replaces everything inside the scroll host, so anything
      // focused in there (an option pill the shopper just clicked) is gone. Put
      // focus somewhere real inside the dialog rather than letting it fall to
      // the document behind the sheet.
      const hadFocusInside = backdrop.contains(document.activeElement);
      scrollHost.innerHTML = renderDetailBody(lastDetail, lastState, esc, item, variantData, variantSelection, anchorItemId, hooks, variantsSettled, adding);
      if (hadFocusInside && !backdrop.contains(document.activeElement)) focusIt(closeBtn);
    };
    repaint();

    // Already on the registry → fetch by itemId so the response includes the
    // item's OWN stored description (e.g. what a shopper typed in the
    // extension's or "Add from Link"'s review form) — the descriptor route
    // below only knows catalog/AI enrichment, never the item's own notes.
    const detailLoad = seedComplete
      ? null
      : ((anchorItemId && BR && BR.API)
        ? BR.API.itemDetails(anchorItemId)
        : fetch(detailUrl, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error("HTTP " + r.status)))));
    if (detailLoad) {
      detailLoad
        .then((data) => {
          // MERGED over what we already had, not swapped for it — see
          // mergeDetail. With no seed `lastDetail` is still the card fallback,
          // so this is the same result the plain replacement used to give.
          lastDetail = mergeDetail(lastDetail, cleanDetail(data && data.detail));
          lastState = (data && data.detail) || detailHasContent(lastDetail) ? "ready" : "error";
          repaint();
        })
        // A seed on screen is real content; "we couldn't load extra details"
        // over the top of it would be a lie about what is known.
        .catch(() => { if (!detailHasContent(lastDetail)) lastState = "error"; repaint(); });
    }

    if (wantVariants) {
      const desc = variantDescriptor(item);
      // Already on the registry → the item-keyed route, whose selectedVariantId
      // is what distinguishes "this exact option is yours" (Remove) from "you
      // have this product but we don't know which option" (Confirm). Not on it
      // → the descriptor route.
      const load = anchorItemId
        ? BR.API.itemVariants(hooks.registryId, anchorItemId)
        : (desc ? BR.API.productVariants(desc) : Promise.resolve(null));
      load
        .then((v) => {
          variantData = v || null;
          variantsSettled = true;
          if (variantData) {
            const groups = variantOptionGroups(variantData);
            if (groups.length) variantSelection = defaultVariantSelection(variantData, groups);
          }
          repaint();
        })
        // No picker — the plain Add still works from what the card knows.
        .catch(() => { variantsSettled = true; repaint(); });
    }

    // Per-variant Add/Remove/Confirm (delegated so it survives repaints).
    // Adding a different option creates a NEW registry item (two variants
    // coexist — the server dedupes by exact variant id); removing pulls that
    // variant's item off the registry.
    scrollHost.addEventListener("click", async (e) => {
      // Option-pill pick: update the selection, re-resolve to a real variant,
      // and repaint the grid + CTA in place (no server call).
      const optBtn = e.target.closest("[data-variant-opt]");
      if (optBtn) {
        e.preventDefault();
        if (!variantData) return;
        const name = optBtn.getAttribute("data-variant-opt");
        const value = optBtn.getAttribute("data-variant-opt-val");
        const groups = variantOptionGroups(variantData);
        const next = { ...(variantSelection || defaultVariantSelection(variantData, groups)), [name]: value };
        variantSelection = repairVariantSelection(variantData, groups, next, name);
        repaint();
        return;
      }
      // CUR-213 — the row chooser's "Choose": hand back and close.
      if (e.target.closest("[data-detail-choose]")) {
        e.preventDefault();
        close();
        if (hooks && typeof hooks.onChoose === "function") hooks.onChoose();
        return;
      }
      // CUR-213 — the plain Add (a product with one option, or none on record).
      const plainAdd = e.target.closest("[data-detail-add]");
      if (plainAdd) {
        e.preventDefault();
        e.stopPropagation();
        if (adding) return;
        adding = true;
        repaint();
        const toastP = (m) => BR && BR.showToast && BR.showToast(m);
        try {
          if (hooks && typeof hooks.add === "function") {
            // The caller's own add (the guided flow: its payload, quantity and
            // bookkeeping); it reports the registry item, or null on failure.
            const created = await hooks.add();
            if (created && created.id) anchorItemId = created.id;
          } else {
            const payload = plainAddPayload(item, variantData, hooks, lastDetail);
            const res = await BR.API.addItem(hooks.registryId, payload);
            const created = res && res.item;
            if (created && created.id) anchorItemId = created.id;
            if (hooks.onAdded) hooks.onAdded(created, payload, res && res.merged);
            toastP(res && res.merged ? "Quantity increased" : "Added to registry");
          }
        } catch (err) {
          toastP((err && err.body && err.body.message) || "Something went wrong");
        }
        adding = false;
        repaint();
        return;
      }
      const addBtn = e.target.closest("[data-variant-add]");
      const remBtn = e.target.closest("[data-variant-remove]");
      const confirmBtn = e.target.closest("[data-variant-confirm]");
      if (!addBtn && !remBtn && !confirmBtn) return;
      e.preventDefault();
      e.stopPropagation();
      if (!wantVariants) return;
      const btn = addBtn || remBtn || confirmBtn;
      btn.disabled = true;
      const toast = (m) => BR.showToast && BR.showToast(m);
      try {
        if (remBtn) {
          const rid = remBtn.getAttribute("data-variant-remove");
          await BR.API.removeItem(hooks.registryId, rid);
          if (anchorItemId === rid) {
            anchorItemId = null;
            if (variantData) variantData.selectedVariantId = null;
          }
          if (hooks.onRemoved) hooks.onRemoved(rid);
          toast("Removed from registry");
        } else if (confirmBtn) {
          // Already on the registry but with an unresolved variant (see
          // renderVariantList's `unresolved` branch) — the registrant is
          // confirming WHICH option they have, so PATCH the existing item
          // rather than adding a second row.
          const rid = confirmBtn.getAttribute("data-variant-confirm");
          const vid = confirmBtn.getAttribute("data-variant-confirm-vid");
          const patch = variantData?.kind === "external"
            ? { externalVariantId: vid }
            : { shopifyVariantId: vid };
          const { item: updated } = await BR.API.updateItem(hooks.registryId, rid, patch);
          // Mark the popup's variant snapshot resolved: `unresolved` (and the
          // Confirm CTA) derive from selectedVariantId, which the fetch-time
          // snapshot left null. Without this the repaint re-renders Confirm and
          // the popup looks like nothing happened.
          if (variantData) variantData.selectedVariantId = vid;
          if (hooks.onConfirmed) hooks.onConfirmed(updated);
          toast("Got it — option confirmed");
        } else {
          const vid = addBtn.getAttribute("data-variant-add");
          const payload = buildVariantAddPayload(vid, variantData, item, lastDetail);
          if (!payload) { btn.disabled = false; return; }
          const res = await BR.API.addItem(hooks.registryId, payload);
          const created = res && res.item;
          if (created && !anchorItemId) {
            anchorItemId = created.id;
            // We know EXACTLY which option was added, so record it on the
            // snapshot. Without this the popup would satisfy `unresolved`
            // (anchored item + null selectedVariantId) the moment the
            // registrant clicked a different pill, and offer "Confirm this is
            // your option" — silently repointing the row they just created
            // instead of adding the second option they asked for.
            if (variantData) variantData.selectedVariantId = vid;
          }
          if (hooks.onAdded) hooks.onAdded(created, payload, res && res.merged);
          toast(res && res.merged ? "Quantity increased" : "Added to registry");
        }
        repaint();
      } catch (err) {
        btn.disabled = false;
        toast((err && err.body && err.body.message) || "Something went wrong");
      }
    });
  }

  function openFromCard(card, hooks) {
    open(descriptorFromCard(card), hooks);
  }

  // ── Variant option grid ─────────────────────────────────────────────────────
  // `variantData` is the response from itemVariants / productVariants
  // ({ kind, variants[, options, selectedVariantId] }). Instead of one
  // Add/Remove row per variant we render one swatch/pill ROW PER OPTION (Color,
  // Size, …) — like the PDP picker — and a single CTA for the selected
  // combination. Ported from registry-unified.js; if you change one of these two
  // renderers, change the other.

  // The variant's option name→value map. External rows carry color/size; Shopify
  // rows carry selectedOptions [{name,value}].
  function variantOptionMap(v, kind) {
    if (kind === "external") {
      const m = {};
      if (v.color) m.Color = v.color;
      if (v.size) m.Size = v.size;
      return m;
    }
    const m = {};
    for (const o of (v.selectedOptions || [])) if (o && o.name) m[o.name] = o.value;
    return m;
  }
  const variantIdOf = (v, kind) => (kind === "external" ? String(v.variantId) : String(v.id));
  const variantIsAvailable = (v, kind) => (kind === "external" ? v.isAvailable !== false : v.availableForSale !== false);

  // The option groups to render: explicit Shopify `options`, or Color/Size
  // synthesized from the external variants. "Title"/"Default Title" is dropped so
  // single-option products don't render a meaningless one-pill row.
  function variantOptionGroups(variantData) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (kind === "external") {
      const groups = [];
      for (const name of ["Color", "Size"]) {
        const values = [...new Set(variants.map((v) => variantOptionMap(v, kind)[name]).filter(Boolean))];
        if (values.length) groups.push({ name, values });
      }
      return groups;
    }
    const opts = Array.isArray(variantData.options) ? variantData.options : [];
    return opts
      .filter((o) => o && o.name && o.name.toLowerCase() !== "title" && Array.isArray(o.values) && o.values.length)
      .map((o) => ({ name: o.name, values: o.values }));
  }

  // Find the variant whose options exactly match `sel` across every group.
  function variantForSelection(variantData, groups, sel) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    return variants.find((v) => {
      const m = variantOptionMap(v, kind);
      return groups.every((g) => m[g.name] === sel[g.name]);
    }) || null;
  }

  // Initial selection: the item's current variant, else the first available, else
  // the first variant — expanded to a full option map.
  // `_groups` is unused — the selection is expanded from the chosen variant's
  // own option map, not from the group list — but every call site passes it
  // alongside variantForSelection/repairVariantSelection, which do need it.
  function defaultVariantSelection(variantData, _groups) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    const sid = variantData.selectedVariantId != null ? String(variantData.selectedVariantId) : null;
    let base = sid ? variants.find((v) => variantIdOf(v, kind) === sid) : null;
    if (!base) base = variants.find((v) => variantIsAvailable(v, kind)) || variants[0];
    return base ? variantOptionMap(base, kind) : {};
  }

  // After a pill click, keep the changed option and re-resolve the others so the
  // selection always points at a REAL variant (prefer an available one). Avoids
  // dead "no such combination" states the way the PDP picker does.
  function repairVariantSelection(variantData, groups, sel, changedName) {
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variantForSelection(variantData, groups, sel)) return sel;
    const honorsChanged = (v) => variantOptionMap(v, kind)[changedName] === sel[changedName];
    const pick = variants.find((v) => variantIsAvailable(v, kind) && honorsChanged(v))
      || variants.find((v) => honorsChanged(v));
    return pick ? variantOptionMap(pick, kind) : sel;
  }

  // Find the registry item (if any) that represents a given variant id, so the
  // grid can render Remove + mark it selected. Variant ids are unique per
  // product, so an id match is a safe identity check across both sources.
  function registryItemForVariant(variantId, kind, hooks) {
    const items = (hooks && hooks.getItems && hooks.getItems()) || [];
    const live = items.filter((it) => it && it.status !== "removed");
    if (kind === "external") {
      const want = String(variantId);
      return live.find((it) => it.externalVariantId != null && String(it.externalVariantId) === want) || null;
    }
    // Shopify variant ids arrive in two shapes: the GID form from the picker and
    // the bare-numeric form from carousel adds / legacy rows. Compare on the
    // numeric tail so both forms match.
    const tail = (v) => String(v == null ? "" : v).replace(/^gid:\/\/shopify\/ProductVariant\//, "");
    const want = tail(variantId);
    return live.find((it) => it.shopifyVariantId != null && tail(it.shopifyVariantId) === want) || null;
  }

  function renderVariantList(variantData, esc, selection, anchorItemId, hooks) {
    if (!variantData) return "";
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variants.length <= 1) return "";
    const groups = variantOptionGroups(variantData);
    if (!groups.length) return "";
    const sel = selection || defaultVariantSelection(variantData, groups);

    // This popup's own item has no specific variant on record — it was added via
    // a path that couldn't identify (or skipped identifying) which option it is.
    // Guessing "the first available variant is the one already on the registry"
    // is exactly the bug this UI must not repeat: every pill would show a false
    // "Add" for an item the registrant already has. Surface it honestly instead
    // — they confirm which option they have, and the click PATCHes that item
    // rather than adding a second row.
    const unresolved = !!anchorItemId && variantData.selectedVariantId == null;

    // A value is selectable when some AVAILABLE variant carries it alongside the
    // other currently-selected options (greys out true dead-ends only).
    const valueSelectable = (group, value) => variants.some((v) => {
      if (!variantIsAvailable(v, kind)) return false;
      const m = variantOptionMap(v, kind);
      if (m[group.name] !== value) return false;
      return groups.every((g) => g.name === group.name || !sel[g.name] || m[g.name] === sel[g.name]);
    });

    const groupsHtml = groups.map((g) => {
      const pills = g.values.map((value) => {
        const isSel = sel[g.name] === value;
        const oos = !valueSelectable(g, value);
        return `<button type="button" class="re-detail__opt${isSel ? " re-detail__opt--sel" : ""}${oos ? " re-detail__opt--oos" : ""}" data-variant-opt="${esc(g.name)}" data-variant-opt-val="${esc(value)}" aria-pressed="${isSel ? "true" : "false"}">${esc(value)}</button>`;
      }).join("");
      return `<div class="re-detail__opt-group">
        <div class="re-detail__opt-lbl">${esc(g.name)}</div>
        <div class="re-detail__opt-row">${pills}</div>
      </div>`;
    }).join("");

    // Resolve the current selection → one variant → a single CTA.
    const resolved = variantForSelection(variantData, groups, sel);
    let ctaHtml;
    let noteHtml = "";
    if (!resolved) {
      ctaHtml = `<button type="button" class="re-detail__variant-cta" disabled>Select options</button>`;
    } else {
      const vid = variantIdOf(resolved, kind);
      const added = unresolved ? null : registryItemForVariant(vid, kind, hooks);
      const available = variantIsAvailable(resolved, kind);
      const priceTxt = resolved.price != null && resolved.price !== "" ? ` · $${Number(resolved.price).toFixed(2)}` : "";
      if (added) {
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" data-variant-remove="${esc(added.id)}"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> In your registry · Remove</button>`;
      } else if (unresolved) {
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--confirm" data-variant-confirm="${esc(anchorItemId)}" data-variant-confirm-vid="${esc(vid)}"${available ? "" : " disabled"}>${available ? `Confirm this is your option${esc(priceTxt)}` : "Sold out"}</button>`;
        noteHtml = `<p class="re-detail__variant-note">This is already in your registry, but we're not sure which option — pick the one you have.</p>`;
      } else if (available) {
        ctaHtml = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--add" data-variant-add="${esc(vid)}"><svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> Add to registry${esc(priceTxt)}</button>`;
      } else {
        ctaHtml = `<button type="button" class="re-detail__variant-cta" disabled>Sold out</button>`;
      }
    }

    return `
      <div class="re-detail__variants">
        <div class="re-detail__variants-lbl">Options</div>
        ${groupsHtml}
        <div class="re-detail__variant-action">${ctaHtml}</div>
        ${noteHtml}
      </div>`;
  }

  // ── CUR-213: an Add on every product ───────────────────────────────────
  // The option picker above only appears for 2+ options, so a single-option
  // product (most Birch items, nearly every partner one) never had an Add in
  // the sheet — the shopper had to close it and use the card. `ctaFor` decides
  // what the sheet offers instead; pure, exported (as _ctaFor) for the tests.
  //
  //   none     — no hooks: view-only (gift-giver, hook-less callers)
  //   choose   — a row chooser (hooks.onChoose); current = already its choice
  //   picker   — 2+ options: renderVariantList owns the CTA
  //   added    — on the registry; removable unless the caller says otherwise
  //   loading  — a Birch product whose option lookup hasn't settled yet
  //   add      — everything else
  function canAdd(hooks) {
    return !!(hooks && (hooks.registryId || typeof hooks.add === "function"));
  }

  function hasPicker(variantData) {
    if (!variantData) return false;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    return variants.length > 1 && variantOptionGroups(variantData).length > 0;
  }

  function addedItemFor(item, hooks, variantData, anchorItemId) {
    if (hooks && typeof hooks.isAdded === "function") return hooks.isAdded() ? { id: anchorItemId || null } : null;
    if (anchorItemId) return { id: anchorItemId };
    const variants = variantData && Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variants.length === 1) {
      const hit = registryItemForVariant(variantIdOf(variants[0], variantData.kind), variantData.kind, hooks);
      if (hit) return hit;
    }
    const live = ((hooks && hooks.getItems && hooks.getItems()) || []).filter((it) => it && it.status !== "removed");
    const tail = (v) => String(v == null ? "" : v).replace(/^gid:\/\/shopify\/Product\//, "");
    if (item.source === "shopify" && item.shopifyProductId) {
      return live.find((it) => it.shopifyProductId != null && tail(it.shopifyProductId) === tail(item.shopifyProductId)) || null;
    }
    if (item.source === "external") {
      return live.find((it) => (item.lushlifeProductId && String(it.lushlifeProductId || "") === String(item.lushlifeProductId))
        || (item.externalUrl && it.externalUrl === item.externalUrl)) || null;
    }
    return null;
  }

  function ctaFor(s) {
    const hooks = s.hooks || null;
    const item = s.item || {};
    if (hooks && typeof hooks.onChoose === "function") {
      return { kind: hooks.isCurrent && hooks.isCurrent() ? "current" : "choose" };
    }
    if (!canAdd(hooks)) return { kind: "none" };
    if (hasPicker(s.variantData)) return { kind: "picker" };
    // Review I3 — a repaint during an add must not offer a second one.
    if (s.adding) return { kind: "adding" };
    const added = addedItemFor(item, hooks, s.variantData, s.anchorItemId || null);
    if (added) return { kind: "added", itemId: added.id || null, removable: hooks.removable !== false && !!added.id && !!hooks.registryId };
    const ownPayload = typeof hooks.add === "function" || typeof hooks.cardPayload === "function";
    if (item.source === "shopify" && !ownPayload && !s.variantsSettled) return { kind: "loading" };
    const variants = s.variantData && Array.isArray(s.variantData.variants) ? s.variantData.variants : [];
    const price = variants.length === 1 && variants[0].price != null && variants[0].price !== ""
      ? Number(variants[0].price)
      : (typeof item.priceCents === "number" ? item.priceCents / 100 : (item.price != null ? Number(item.price) : null));
    return { kind: "add", price: Number.isFinite(price) && price > 0 ? price : null };
  }

  const PLUS_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>';
  const CHECK_SVG = '<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';

  function plainCtaHtml(c, esc) {
    let btn = "";
    if (c.kind === "add") {
      btn = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--add" data-detail-add>${PLUS_SVG} Add to registry${c.price != null ? ` · $${c.price.toFixed(2)}` : ""}</button>`;
    } else if (c.kind === "loading") {
      btn = `<button type="button" class="re-detail__variant-cta" disabled>Add to registry</button>`;
    } else if (c.kind === "adding") {
      btn = `<button type="button" class="re-detail__variant-cta" disabled>Adding…</button>`;
    } else if (c.kind === "added") {
      btn = c.removable
        ? `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" data-variant-remove="${esc(c.itemId)}">${CHECK_SVG} In your registry · Remove</button>`
        : `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" disabled>${CHECK_SVG} In your registry</button>`;
    } else if (c.kind === "choose") {
      btn = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--add" data-detail-choose>Choose</button>`;
    } else if (c.kind === "current") {
      btn = `<button type="button" class="re-detail__variant-cta re-detail__variant-cta--remove" disabled>${CHECK_SVG} Current choice</button>`;
    }
    return btn ? `<div class="re-detail__variant-action re-detail__variant-action--plain">${btn}</div>` : "";
  }

  /** What the plain Add sends: the caller's own card payload when it has one
   *  (exactly what the card's "+ Add" sends), else the single option, else the
   *  listing itself — the same shape a browse card adds. */
  function plainAddPayload(item, variantData, hooks, detail) {
    if (hooks && typeof hooks.cardPayload === "function") {
      const own = hooks.cardPayload();
      if (own) return own;
    }
    const variants = variantData && Array.isArray(variantData.variants) ? variantData.variants : [];
    if (variants.length === 1) {
      const p = buildVariantAddPayload(variantIdOf(variants[0], variantData.kind), variantData, item, detail);
      if (p) return p;
    }
    const cents = typeof item.priceCents === "number" ? item.priceCents
      : (item.price != null && Number.isFinite(Number(item.price)) ? Math.round(Number(item.price) * 100) : undefined);
    if (item.source === "external") {
      return {
        source: "external",
        title: item.title || (detail && detail.title) || "",
        imageUrl: item.imageUrl || (detail && detail.image) || undefined,
        priceCents: cents,
        currency: item.currency || "USD",
        productType: item.productType || undefined,
        externalUrl: item.externalUrl || (detail && detail.externalUrl) || undefined,
        externalRetailer: item.externalRetailer || undefined,
        lushlifeProductId: item.lushlifeProductId || undefined,
        trustScore: item.trustScore != null ? Number(item.trustScore) : undefined,
        priority: "nice_to_have",
        quantityWanted: 1,
      };
    }
    return {
      source: "shopify",
      shopifyProductId: item.shopifyProductId || undefined,
      shopifyVariantId: null,
      shopifyHandle: item.shopifyHandle || undefined,
      title: item.title || "",
      imageUrl: item.imageUrl || undefined,
      priceCents: cents,
      currency: item.currency || "USD",
      priority: "nice_to_have",
      quantityWanted: 1,
    };
  }

  /** CUR-213 — hooks that act THROUGH a card's own add, addressed by the
   *  PRODUCT, not by the card node the sheet was opened from (review C1: a
   *  repaint can replace that node while the sheet is open):
   *    findBtn()   — the card's add button for this product right now, or null
   *    addFn(btn)  — the card's own add; resolves true on success, false not
   *    isAdded()   — the product's added state, read from the page/registry
   *  A missing button is never "added". No Remove: the card owns that. Used by
   *  the guided flow and the browse page. */
  function hooksViaCardButton(findBtn, addFn, isAddedFn) {
    return {
      add() {
        const btn = findBtn();
        if (!btn || btn.disabled) return Promise.resolve(null);
        return Promise.resolve(addFn(btn)).then((ok) => (ok ? { id: null } : null), () => null);
      },
      isAdded: () => !!isAddedFn(),
      removable: false,
    };
  }

  // Build the addItem payload for a chosen variant. Pulls product-level context
  // (handle, productType, lushlife id, retailer) from the popup's product `item`
  // and the variant-level fields (id, price, color/size, image) from
  // `variantData`. The variant label is appended to the title so two variants of
  // the same product read as distinct rows on the registry.
  function buildVariantAddPayload(variantId, variantData, product, detail) {
    if (!variantData) return null;
    const kind = variantData.kind;
    const variants = Array.isArray(variantData.variants) ? variantData.variants : [];
    const baseTitle = String((product && product.title) || (detail && detail.title) || "");
    const productType = (product && product.productType) || undefined;
    const currency = (product && product.currency) || (detail && detail.currency) || "USD";
    const cents = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Math.round(Number(v) * 100) : undefined);
    const withLabel = (label) => (label && !baseTitle.toLowerCase().includes(label.toLowerCase()) ? `${baseTitle} — ${label}` : baseTitle);
    if (kind === "external") {
      const v = variants.find((x) => String(x.variantId) === String(variantId));
      if (!v) return null;
      const label = [v.color, v.size].filter(Boolean).join(" · ");
      return {
        source: "external",
        title: withLabel(label),
        // The bound variant's own photo, never the catalog's product-level
        // default — a "White" row wearing the blue listing photo reads as the
        // wrong product.
        imageUrl: v.image || (product && product.imageUrl) || (detail && detail.image) || undefined,
        priceCents: cents(v.price),
        currency,
        productType,
        externalUrl: v.buyLink || (product && product.externalUrl) || undefined,
        externalRetailer: (product && product.externalRetailer) || variantData.retailer || undefined,
        externalVariantId: String(v.variantId),
        externalColor: v.color || undefined,
        externalSize: v.size || undefined,
        lushlifeProductId: (product && product.lushlifeProductId) || undefined,
        priority: "nice_to_have",
        quantityWanted: 1,
      };
    }
    const v = variants.find((x) => String(x.id) === String(variantId));
    if (!v) return null;
    const label = v.title && v.title !== "Default Title" ? v.title : "";
    return {
      source: "shopify",
      title: withLabel(label),
      imageUrl: v.image || (product && product.imageUrl) || (detail && detail.image) || undefined,
      priceCents: cents(v.price),
      currency,
      productType,
      shopifyProductId: (product && product.shopifyProductId) || variantData.productId || undefined,
      shopifyVariantId: String(v.id),
      shopifyHandle: (product && product.shopifyHandle) || undefined,
      priority: "nice_to_have",
      quantityWanted: 1,
    };
  }

  // TrustScore block for the popup — element for element the same as
  // registry-unified.js's renderTrustBlock, so the intake walkthrough shows the
  // SAME card the editor does: the overall number plus the per-criterion
  // breakdown (Safety / Ease / Comfort / Longevity / Value) with bars, and a
  // "View details" disclosure carrying each criterion's reasoning. Scores are
  // 0-100. Both live in registry.css (.re-detail__bd-*); if you change one of
  // these two renderers, change the other.
  //
  // Renders nothing when there is neither a score nor a breakdown.
  function renderTrustBlock(d, esc) {
    const raw = d.trustScore;
    const overall = raw != null && Number.isFinite(Number(raw)) ? Math.round(Math.max(0, Math.min(100, Number(raw)))) : null;
    const bd = Array.isArray(d.trustBreakdown)
      ? d.trustBreakdown.filter((c) => c && c.criteria != null && Number.isFinite(Number(c.score)))
      : [];
    if (overall == null && !bd.length) return "";
    const head = overall != null
      ? `<div class="re-detail__trust-head"><span class="re-detail__trust-num">${overall}</span><span class="re-detail__trust-lbl">TrustScore</span></div>`
      : "";
    if (!bd.length) {
      return `<div class="re-detail__trust">${head}</div>`;
    }
    const clamp = (n) => Math.round(Math.max(0, Math.min(100, Number(n))));
    const rows = bd.map((c) => {
      const s = clamp(c.score);
      return `<li class="re-detail__bd-item">
        <div class="re-detail__bd-row"><span class="re-detail__bd-name">${esc(String(c.criteria))}</span><span class="re-detail__bd-score">${s}</span></div>
        <div class="re-detail__bd-track" role="progressbar" aria-valuenow="${s}" aria-valuemin="0" aria-valuemax="100" aria-label="${esc(String(c.criteria))} score ${s} out of 100"><span class="re-detail__bd-fill" style="width:${s}%"></span></div>
      </li>`;
    }).join("");
    const withWhy = bd.filter((c) => c.reasoning);
    const details = withWhy.length
      ? `<details class="re-detail__bd-details"><summary class="re-detail__bd-toggle"><span class="re-detail__bd-toggle-show">View details</span><span class="re-detail__bd-toggle-hide">Hide details</span></summary>
          <div class="re-detail__bd-why">${withWhy.map((c) => {
            const wpct = Number.isFinite(Number(c.weight)) ? ` <span class="re-detail__bd-weight">(${Math.round(Number(c.weight) * 100)}% of score)</span>` : "";
            return `<p><strong>${esc(String(c.criteria))}.</strong> ${esc(String(c.reasoning))}${wpct}</p>`;
          }).join("")}</div>
        </details>`
      : "";
    return `<div class="re-detail__trust re-detail__trust--card">
      ${head}
      <div class="re-detail__bd"><ul class="re-detail__bd-list">${rows}</ul>${details}</div>
    </div>`;
  }

  function renderDetailBody(d, state, esc, item, variantData, variantSelection, anchorItemId, hooks, variantsSettled, adding) {
    const money = (v, c) => (v == null ? "" : new Intl.NumberFormat("en-US", { style: "currency", currency: c || "USD" }).format(Number(v)));
    // Show the brand only. No fallback label — when the brand is unknown the
    // line renders blank (a non-breaking space below) so the card keeps its
    // height and the layout stays consistent.
    const subtitle = d.brand || "";
    const list = (arr) => Array.isArray(arr) ? arr.filter(Boolean) : [];
    const features = list(d.keyFeatures), pros = list(d.pros), cons = list(d.cons);
    const specs = Array.isArray(d.specifications) ? d.specifications : [];
    const certs = list(d.certifications), materials = list(d.materials);
    const description = typeof d.description === "string" ? d.description.trim() : "";
    const hasAny = description || features.length || pros.length || cons.length || specs.length || certs.length || materials.length;

    const birchPdp = d.source === "shopify" && item.shopifyHandle
      ? `/products/${encodeURIComponent(item.shopifyHandle)}` : null;

    const trustHtml = renderTrustBlock(d, esc);

    const descHtml = description ? `
      <section class="re-detail__section">
        <h3>Description</h3>
        <p class="re-detail__desc">${esc(description)}</p>
      </section>` : "";

    const featuresHtml = features.length ? `
      <section class="re-detail__section">
        <h3>Key features</h3>
        <ul class="re-detail__feats">${features.map((f) => `<li>${esc(f)}</li>`).join("")}</ul>
      </section>` : "";

    const prosConsHtml = (pros.length || cons.length) ? `
      <section class="re-detail__section">
        <h3>Pros &amp; cons</h3>
        <div class="re-detail__pc">
          ${pros.length ? `<div class="re-detail__pc-col"><div class="re-detail__pc-head re-detail__pc-head--pro">Pros</div><ul>${pros.map((p) => `<li>${esc(p)}</li>`).join("")}</ul></div>` : ""}
          ${cons.length ? `<div class="re-detail__pc-col"><div class="re-detail__pc-head re-detail__pc-head--con">Cons</div><ul>${cons.map((c) => `<li>${esc(c)}</li>`).join("")}</ul></div>` : ""}
        </div>
      </section>` : "";

    const specsHtml = specs.length ? `
      <section class="re-detail__section">
        <h3>Specifications</h3>
        <dl class="re-detail__specs">${specs.map((row) => {
          const label = Array.isArray(row) ? row[0] : row && row.label;
          const value = Array.isArray(row) ? row[1] : row && row.value;
          return `<div class="re-detail__spec-row"><dt>${esc(String(label ?? ""))}</dt><dd>${esc(String(value ?? ""))}</dd></div>`;
        }).join("")}</dl>
      </section>` : "";

    const chips = (label, items) => items.length ? `
      <div class="re-detail__chips-block"><div class="re-detail__chips-lbl">${label}</div><div class="re-detail__chips">${items.map((x) => `<span class="re-detail__chip">${esc(x)}</span>`).join("")}</div></div>` : "";
    const moreHtml = (certs.length || materials.length) ? `
      <section class="re-detail__section"><h3>More</h3>${chips("Certifications", certs)}${chips("Materials", materials)}</section>` : "";

    const skeleton = `<div class="re-detail__skeleton"><span></span><span></span><span></span></div>`;
    const emptyMsg = state === "error"
      ? `<p class="re-detail__empty">We couldn’t load extra details right now.</p>`
      : (state === "ready" && !hasAny ? `<p class="re-detail__empty">No additional details available yet.</p>` : "");

    // Reflect the picker's currently-selected variant in the header image, price
    // and buy link. `d` (the product-level detail/fallback) never changes as
    // pills are clicked, so without this the top stays on the default option
    // while the grid and CTA below update — reads as a bug. Fall back to `d`'s
    // values when the selected variant carries none (some scraped externals).
    let displayImage = d.image;
    let displayPrice = d.price;
    // Suppressed for an external item in gift-giver mode (hooks.hideExternalLink)
    // — no route out to the retailer. birchPdp is Birch's own product page, not
    // a retailer link, so a Shopify item's "View product" is unaffected.
    let displayUrl = (hooks && hooks.hideExternalLink && item.source === "external")
      ? null
      : (d.externalUrl || birchPdp);
    if (variantData) {
      const groups = variantOptionGroups(variantData);
      if (groups.length) {
        const sel = variantSelection || defaultVariantSelection(variantData, groups);
        const resolved = variantForSelection(variantData, groups, sel);
        if (resolved) {
          if (resolved.image) displayImage = resolved.image;
          if (resolved.price != null && resolved.price !== "") displayPrice = Number(resolved.price);
          if (resolved.buyLink) displayUrl = resolved.buyLink;
        }
      }
    }

    return `
      <div class="re-detail__cols">
        <div class="re-detail__media">
          ${displayImage ? `<img src="${esc(displayImage)}" alt="" class="re-detail__img">` : `<div class="re-detail__img-ph">🌿</div>`}
          <div class="re-detail__price">${money(displayPrice, d.currency)}</div>
          ${renderVariantList(variantData, esc, variantSelection, anchorItemId, hooks) || plainCtaHtml(ctaFor({ item, hooks, variantData, variantsSettled, anchorItemId, adding }), esc)}
          ${trustHtml}
        </div>
        <div class="re-detail__main">
          <h2 class="re-detail__title">${esc(d.title || "")}</h2>
          <div class="re-detail__sub">${esc(subtitle) || "&nbsp;"}</div>
          ${state === "loading" ? skeleton : `${descHtml}${featuresHtml}${prosConsHtml}${specsHtml}${moreHtml}${emptyMsg}`}
        </div>
      </div>
      ${displayUrl ? `<div class="re-detail__foot"><a class="re-detail__buylink" href="${esc(displayUrl)}" target="_blank" rel="noopener">${d.source === "shopify" ? "View product" : `View at ${esc(d.brand || "retailer")}`}<span class="re-detail__buylink-ext" aria-hidden="true">↗</span></a></div>` : ""}`;
  }

  window.BirchDetailPopup = {
    open, openFromCard, descriptorFromCard,
    // CUR-213 — pure; exported for unit/detail-popup-add.test.cjs.
    _ctaFor: ctaFor, _plainCtaHtml: plainCtaHtml, _plainAddPayload: plainAddPayload,
    hooksViaCardButton,
  };
})();
